// useNavbarOculta.js — o cabeçalho sai do caminho quando a pessoa está lendo.
//
// POR QUE ISTO IMPORTA MAIS DO QUE PARECE:
// o header do site tem três faixas (contato, logo + busca, categorias) e ocupa
// **204px no celular** — mais de um quarto de uma tela de 6 polegadas, o tempo
// todo, em toda página. Numa lista de livros isso é desperdício; dentro do
// leitor é a diferença entre ver meia página e ver a página.
//
// A REGRA, na ordem em que é avaliada:
//   1. no topo da página, o cabeçalho SEMPRE aparece;
//   2. rolando para BAIXO (procurando conteúdo), ele sai;
//   3. rolando para CIMA (procurando navegação), ele volta na hora.
//
// A terceira é a que faz o recurso não irritar: quem quer o menu não precisa
// voltar ao topo da página, só fazer o gesto de subir.
//
// ELE SAI COM `transform`, E NÃO COM `display`. Sumir de verdade mudaria a
// altura do documento e faria a página inteira pular para cima — o conteúdo
// continua reservando `--nav-h` de espaço, e só o desenho do cabeçalho desliza
// para fora da tela.
import { useEffect } from "react";

/** Movimento mínimo para valer como intenção. Abaixo disso é tremor. */
const LIMIAR = 8;

/** Até esta altura a página conta como "no topo". */
const ZONA_DO_TOPO = 24;

export default function useNavbarOculta(ativo = true) {
  useEffect(() => {
    const raiz = document.documentElement;

    if (!ativo) {
      raiz.classList.remove("nav-oculto");
      // Sem o recurso ligado (no leitor, por exemplo), a faixa volta ao normal:
      // a classe some e a regra do CSS deixa de valer.
      raiz.classList.add("pagina-no-topo");
      return;
    }

    let ultimoY = window.scrollY;
    let agendado = false;

    const avaliar = () => {
      agendado = false;
      const y = window.scrollY;

      // Perto do fim da página o navegador "estica" a rolagem em alguns
      // aparelhos, e isso gera movimentos falsos para cima e para baixo. Sem
      // esta guarda, o cabeçalho pisca no rodapé de toda página.
      const fim = document.documentElement.scrollHeight - window.innerHeight;
      if (y < 0 || y > fim + 4) return;

      // A FAIXA LARANJA só existe no alto da página (pedido do dono,
      // 11/09/2026). Ela é contato, FAQ e redes sociais: serve para quem
      // CHEGA, não para quem já está lendo. Enquanto a pessoa estiver fora do
      // topo, o cabeçalho sobe a altura dela e a faixa sai de cena, mesmo
      // quando a barra principal volta ao rolar para cima.
      raiz.classList.toggle("pagina-no-topo", y <= ZONA_DO_TOPO);

      if (y <= ZONA_DO_TOPO) {
        raiz.classList.remove("nav-oculto");
      } else if (y > ultimoY + LIMIAR) {
        raiz.classList.add("nav-oculto");
      } else if (y < ultimoY - LIMIAR) {
        raiz.classList.remove("nav-oculto");
      }

      ultimoY = y;
    };

    const aoRolar = () => {
      // Uma avaliação por quadro: o evento de rolagem dispara dezenas de vezes
      // por segundo, e mexer em classe do `<html>` a cada disparo força o
      // navegador a recalcular estilo sem necessidade.
      if (agendado) return;
      agendado = true;
      window.requestAnimationFrame(avaliar);
    };

    // Quem foca a busca (ou qualquer campo do cabeçalho) precisa vê-lo. Isso
    // acontece de verdade: o teclado do celular rola a página ao abrir, e sem
    // esta linha o campo que a pessoa acabou de tocar sairia da tela.
    const aoFocar = (e) => {
      if (e.target && e.target.closest && e.target.closest("header")) {
        raiz.classList.remove("nav-oculto");
      }
    };

    window.addEventListener("scroll", aoRolar, { passive: true });
    document.addEventListener("focusin", aoFocar);

    avaliar();

    return () => {
      window.removeEventListener("scroll", aoRolar);
      document.removeEventListener("focusin", aoFocar);
      raiz.classList.remove("nav-oculto");
    };
  }, [ativo]);
}
